import { PointerEventTypes } from '@babylonjs/core/p../../Events/pointerEvents';
import {
    Vector3,
    Quaternion,
} from '@babylonjs/core/Maths/math.vector';

export default class CameraRotationController {
    constructor(scene, camera, options = {}) {
        this.scene = scene;
        this.camera = camera;
        this.cameraInitialAlpha = camera.alpha;
        this.cameraInitialBeta = camera.beta;
        this.isPointerDown = false;
        this.springAnimationFrames = [];

        // Configuration options with defaults
        this.springDuration = options.springDuration || 1000; // 1 second in ms
        this.springDelay = options.springDelay || 500; // 500ms delay before spring starts
        this.horizontalBias = options.horizontalBias || 0; // How much to favor horizontal movements (0-1)
        this.directionThreshold = options.directionThreshold || 4; // Pixel threshold before locking direction
        this.horizontalBiasThreshold = options.horizontalBiasThreshold || 0.5; // Above this ratio is "horizontal"
        this.verticalBiasThreshold = options.verticalBiasThreshold || 0.5; // Above this ratio is "vertical"
        this.horizontalRatioWhenVerticalSwipe = 0.7;
        this.verticalRatioWhenHorizontalSwipe = 0.1;

        // For tracking pointer movement
        this.initialPointerX = null;
        this.initialPointerY = null;
        this.currentPointerX = null;
        this.currentPointerY = null;
        this.initialCameraAlpha = null;
        this.initialCameraBeta = null;
        this.totalCameraAlpha = camera.alpha;
        this.totalCameraBeta = camera.beta;

        this.isProgrammaticRotation = false;
        this.programmaticAnimationFrame = null;

        // For tracking delay timer
        this.delayTimeout = null;

        // Animation callback.
        this.animating = false;
        this.onAnimating = null;

        // Track pointer states
        scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
                this.isPointerDown = true;

                // Record initial pointer position
                this.initialPointerX = pointerInfo.event.clientX;
                this.initialPointerY = pointerInfo.event.clientY;
                this.currentPointerX = pointerInfo.event.clientX;
                this.currentPointerY = pointerInfo.event.clientY;

                this.gestureDirectionDetermined = false;
                this.isHorizontalGesture = false;
                this.isVerticalGesture = false;

                // Record initial camera angles
                this.initialCameraAlpha = this.camera.alpha;
                this.initialCameraBeta = this.camera.beta;
                // this.totalCameraAlpha = this.camera.alpha;
                // this.totalCameraBeta = this.camera.beta;

                // Cancel any ongoing animations
                this.springAnimationFrames.forEach((frame) => this.scene.unregisterBeforeRender(frame));
                this.springAnimationFrames = [];

                // Cancel any pending delay
                if (this.delayTimeout) {
                    clearTimeout(this.delayTimeout);
                    this.delayTimeout = null;
                }
            } else if (pointerInfo.type === PointerEventTypes.POINTERMOVE && this.isPointerDown) {
                // Update current pointer position
                this.currentPointerX = pointerInfo.event.clientX;
                this.currentPointerY = pointerInfo.event.clientY;

                // If we haven't determined the gesture direction yet
                if (!this.gestureDirectionDetermined && this.initialPointerX != null) {
                    const currentX = pointerInfo.event.clientX;
                    const currentY = pointerInfo.event.clientY;

                    // Calculate movement distance
                    const deltaX = Math.abs(currentX - this.initialPointerX);
                    const deltaY = Math.abs(currentY - this.initialPointerY);
                    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                    // Once we've moved past the threshold, determine the gesture type
                    if (totalMovement >= this.directionThreshold) {
                        this.gestureDirectionDetermined = true;

                        // Calculate ratio of horizontal movement
                        const horizontalRatio = deltaX / (deltaX + deltaY);

                        // Set gesture type based on horizontal ratio
                        this.isHorizontalGesture = horizontalRatio >= this.horizontalBiasThreshold;

                        if (this.isHorizontalGesture) {
                            this.isVerticalGesture = false;
                        } else {
                            // this.isVerticalGesture = false;

                            const verticalRatio = deltaY / (deltaX + deltaY);

                            // Set gesture type based on vertical ratio
                            this.isVerticalGesture = verticalRatio >= this.verticalBiasThreshold;
                        }
                    }
                }
            } else if (pointerInfo.type === PointerEventTypes.POINTERUP) {
                this.isPointerDown = false;

                // Reset pointer tracking
                this.initialPointerX = null;
                this.initialPointerY = null;
                this.currentPointerX = null;
                this.currentPointerY = null;
                this.initialCameraAlpha = null;
                this.initialCameraBeta = null;

                // Schedule spring-back with delay
                this.scheduleSpringBack();
            }
        });
    }

    rotateTo(targetAlpha, targetBeta, options = {}) {
        // Default options
        const duration = options.duration || 1000; // ms
        const easing = options.easing || this.easeOutCubic; // Default easing function
        const onComplete = options.onComplete || null; // Optional callback

        // Cancel any existing animations
        this.cancelAllAnimations();

        // Find all adjust nodes
        const adjustNodes = this.scene.transformNodes
            .concat(this.scene.meshes)
            .filter((node) => !node.parent && node.name.endsWith('->orient'));

        if (adjustNodes.length === 0) {
            return;
        }

        // Calculate shortest path
        const findShortestRotation = (currentAngle, targetAngle) => {
            const twoPI = Math.PI * 2;

            // Normalize angles to [0, 2π]
            const normalizedCurrent = ((currentAngle % twoPI) + twoPI) % twoPI;
            const normalizedTarget = ((targetAngle % twoPI) + twoPI) % twoPI;

            // Calculate both clockwise and counter-clockwise distances
            const clockwiseDist = normalizedTarget >= normalizedCurrent
                ? normalizedTarget - normalizedCurrent
                : twoPI - (normalizedCurrent - normalizedTarget);

            const counterClockwiseDist = normalizedCurrent >= normalizedTarget
                ? normalizedCurrent - normalizedTarget
                : twoPI - (normalizedTarget - normalizedCurrent);

            // Return the shorter distance and its sign
            if (clockwiseDist <= counterClockwiseDist) {
                return clockwiseDist; // Positive for clockwise
            }
                return -counterClockwiseDist; // Negative for counter-clockwise
        };

        // Store initial state and prepare final states
        const startTime = performance.now();

        const nodeStates = adjustNodes.map((node) => {
            // Get initial rotation quaternion
            const initialRotation = node.rotationQuaternion
                ? node.rotationQuaternion.clone()
                : Quaternion.FromEulerAngles(node.rotation.x, node.rotation.y, node.rotation.z);

            // Extract current rotation
            const currentAlpha = node.metadata?.lastAlphaRotation || 0;

            // Calculate shortest angular delta
            const deltaAlpha = findShortestRotation(currentAlpha, targetAlpha - this.cameraInitialAlpha);

            // Calculate new absolute alpha
            const newAlpha = currentAlpha + deltaAlpha;

            // Create final rotation quaternion (with targetBeta for tilt)
            // We're using quaternion composition to maintain proper rotation order
            const finalRotation = Quaternion.RotationYawPitchRoll(
                newAlpha, // Alpha (yaw) - horizontal rotation
                0, // targetBeta, // Beta (pitch) - vertical tilt
                0, // targetBeta, // Gamma (roll) - keep at zero
            );

            if (node.metadata.initialRotation == null) {
                node.metadata.initialRotation = node.rotation;
                node.metadata.initialRotationQuaternion = node.rotationQuaternion;
            }

            return {
                node,
                initialRotation,
                finalRotation,
                newAlpha,
            };
        });

        // Flag that we're in a programmatic rotation
        this.isProgrammaticRotation = true;

        // Animation frame function
        const animateFrame = () => {
            const currentTime = performance.now();
            const elapsedTime = currentTime - startTime;

            if (elapsedTime >= duration) {
                // Animation complete - apply final rotations
                for (const state of nodeStates) {
                    // Apply final rotation
                    state.node.rotationQuaternion = state.finalRotation.clone();

                    // Update the lastAlpha for spring-back reference
                    state.node.metadata.lastAlphaRotation = state.newAlpha;
                }

                // Clean up
                this.scene.unregisterBeforeRender(animateFrame);
                this.programmaticAnimationFrame = null;
                this.isProgrammaticRotation = false;

                this.animating = false;
                if (this.onAnimating) {
                    this.onAnimating(false);
                }

                // Call completion callback if provided
                if (onComplete) {
                    onComplete();
                }

                return;
            }

            // Calculate progress with easing
            const progress = elapsedTime / duration;
            const easedProgress = easing(progress);

            // Apply interpolated rotation to each node
            for (const state of nodeStates) {
                // Use spherical linear interpolation (slerp) for smooth rotation
                const interpolatedRotation = Quaternion.Slerp(
                    state.initialRotation,
                    state.finalRotation,
                    easedProgress,
                );

                // Apply to node
                state.node.rotationQuaternion = interpolatedRotation;
            }
        };

        // Register animation frame
        this.programmaticAnimationFrame = animateFrame;
        this.scene.registerBeforeRender(animateFrame);

        this.animating = true;
        if (this.onAnimating) {
            this.onAnimating(true);
        }
    }

    // Ease out cubic function
    // eslint-disable-next-line class-methods-use-this
    easeOutCubic(x) {
        return 1 - (1 - x) ** 3;
    }

    cancelAllAnimations() {
        // Cancel spring animations
        this.springAnimationFrames.forEach((frame) => this.scene.unregisterBeforeRender(frame));
        this.springAnimationFrames = [];

        // Cancel programmatic animation
        if (this.programmaticAnimationFrame) {
            this.scene.unregisterBeforeRender(this.programmaticAnimationFrame);
            this.programmaticAnimationFrame = null;
        }

        // Cancel pending spring delay
        if (this.delayTimeout) {
            clearTimeout(this.delayTimeout);
            this.delayTimeout = null;
        }

        this.isProgrammaticRotation = false;

        this.animating = false;
    }

    applyCameraToObject() {
        // Skip updates during programmatic rotations
        if (this.isProgrammaticRotation) {
            return;
        }

        if (!this.isPointerDown && this.springAnimationFrames.length > 0) {
            // Skip manual updates during spring animation
            return;
        }

        let deltaAlpha = this.camera.alpha - this.cameraInitialAlpha;
        const deltaBeta = this.camera.beta - this.cameraInitialBeta;

        if (deltaAlpha === 0 && deltaBeta === 0) {
            return;
        }

        this.totalCameraAlpha += deltaAlpha;
        this.totalCameraBeta += deltaBeta;

        // Find all adjust nodes
        const adjustNodes = this.scene.transformNodes
            .concat(this.scene.meshes)
            .filter((node) => !node.parent && node.name.endsWith('->orient'));

        let adjustedDeltaBeta = deltaBeta;

        // Calculate directional bias based on total gesture movement
        if (this.isPointerDown && this.initialPointerX != null) {
            // Calculate total movement from initial point
            const totalDeltaX = Math.abs(this.currentPointerX - this.initialPointerX);
            const totalDeltaY = Math.abs(this.currentPointerY - this.initialPointerY);
            const totalMovement = totalDeltaX + totalDeltaY;

            if (totalMovement > 5) { // Threshold to avoid jitter on tiny movements
                // Calculate how horizontal the movement is (0-1)
                const horizontalRatio = totalDeltaX / totalMovement;

                // If horizontal movement is dominant (beyond our bias threshold)
                if (horizontalRatio > this.horizontalBias) {
                    // Calculate how far beyond the threshold (0-1 range)
                    const dominanceStrength = (horizontalRatio - this.horizontalBias) / (1 - this.horizontalBias);

                    // Apply quadratic reduction to vertical movement (smoother feel)
                    const reductionFactor = 1 - dominanceStrength ** 2;
                    adjustedDeltaBeta = deltaBeta * reductionFactor;
                }
            }
        }

        // If gesture direction is determined and it's horizontal, ignore vertical movement
        if (this.gestureDirectionDetermined && this.isHorizontalGesture) {
            adjustedDeltaBeta *= this.verticalRatioWhenHorizontalSwipe;
        }

        if (this.gestureDirectionDetermined && this.isVerticalGesture) {
            // If vertical gesture, radically reduce sensitivity in horizontal.
            deltaAlpha *= this.horizontalRatioWhenVerticalSwipe;
        }

        // Apply rotations to nodes
        this.applyRotationsToNodes(adjustNodes, deltaAlpha, adjustedDeltaBeta);

        // Reset camera position
        this.camera.alpha = this.cameraInitialAlpha;
        this.camera.beta = this.cameraInitialBeta;
    }

    /**
     * Temporarily reset rotations.
     */
    suspendRotation() {
        this.isSuspended = true;

        // Find all adjust nodes
        const adjustNodes = this.scene.transformNodes
            .concat(this.scene.meshes)
            .filter((node) => !node.parent && node.name.endsWith('->orient'));

        for (const node of adjustNodes) {
            if (node.metadata == null) {
                node.metadata = {};
            }

            if (node.metadata.initialRotation != null) {
                node.metadata.currentRotation = node.rotation;
                node.metadata.currentRotationQuaternion = node.rotationQuaternion;

                node.rotation = node.metadata.initialRotation;
                node.rotationQuaternion = node.metadata.initialRotationQuaternion;
            }
        }
    }

    /**
     * Resumes suspended rotations.
     */
    resumeRotation() {
        this.isSuspended = false;

        // Find all adjust nodes
        const adjustNodes = this.scene.transformNodes
            .concat(this.scene.meshes)
            .filter((node) => !node.parent && node.name.endsWith('->orient'));

        for (const node of adjustNodes) {
            if (node.metadata == null) {
                node.metadata = {};
            }

            if (node.metadata.currentRotation != null) {
                node.rotation = node.metadata.currentRotation;
                node.rotationQuaternion = node.metadata.currentRotationQuaternion;
            }
        }
    }

    applyRotationsToNodes(nodes, deltaAlpha, deltaBeta) {
        // Calculate a view-aligned right vector for the tilt axis
        const cameraDirection = this.camera.getDirection(Vector3.Forward());
        const worldUp = Vector3.Up();
        const rightAxis = Vector3.Cross(worldUp, cameraDirection).normalize();

        for (const node of nodes) {
            // For Y rotation (horizontal), use quaternions to avoid gimbal lock
            const yRotation = Quaternion.RotationAxis(Vector3.Up(), deltaAlpha);

            // For tilt rotation (vertical), use the view-aligned right axis
            const tiltRotation = Quaternion.RotationAxis(rightAxis, deltaBeta);

            // Combine rotations (order matters!)
            const combinedRotation = yRotation.multiply(tiltRotation);

            // Save initial rotations.
            if (node.metadata == null) {
                node.metadata = {};
            }

            if (node.metadata.initialRotation == null) {
                node.metadata.initialRotation = node.rotation;
                node.metadata.initialRotationQuaternion = node.rotationQuaternion;
            }

            // Apply to node
            if (!node.rotationQuaternion) {
                // Convert existing Euler rotation to quaternion if needed
                node.rotationQuaternion = Quaternion.FromEulerAngles(
                    node.rotation.x,
                    node.rotation.y,
                    node.rotation.z,
                );
            }

            // Apply our rotation
            node.rotationQuaternion = combinedRotation.multiply(node.rotationQuaternion);

            // Store current alpha rotation for spring-back
            if (node.metadata.lastAlphaRotation == null) {
                node.metadata.lastAlphaRotation = 0;
            }

            node.metadata.lastAlphaRotation += deltaAlpha;
        }
    }

    scheduleSpringBack() {
        // Cancel any existing delay
        if (this.delayTimeout) {
            clearTimeout(this.delayTimeout);
        }

        // Set a new delay timer
        this.delayTimeout = setTimeout(() => {
            // After delay, start the spring animation
            this.startSpringBack();
            this.delayTimeout = null;
        }, this.springDelay);
    }

    startSpringBack() {
        // Skip updates during programmatic rotations
        if (this.isProgrammaticRotation) { return; }

        // Find all adjust nodes
        const adjustNodes = this.scene.transformNodes
            .concat(this.scene.meshes)
            .filter((node) => !node.parent && node.name.endsWith('->orient'));

        // Store initial quaternions for each node
        const initialRotations = adjustNodes.map((node) => ({
            node,
            rotation: node.rotationQuaternion ? node.rotationQuaternion.clone() : null,

            lastAlpha: node.metadata?.lastAlphaRotation || 0,
        }));

        // Start time for animation
        const startTime = performance.now();

        // Create animation frame
        const animationFrame = () => {
            if (this.isPointerDown) {
                // Remove animation frame
                this.scene.unregisterBeforeRender(animationFrame);
                return;
            }

            const currentTime = performance.now();
            const elapsedTime = currentTime - startTime;

            if (elapsedTime >= this.springDuration) {
                // Animation complete, set final rotations
                for (const item of initialRotations) {
                    // Create a rotation that preserves only the Y-axis rotation
                    const finalRotation = Quaternion.RotationAxis(
                        Vector3.Up(),
                        item.lastAlpha,
                    );
                    item.node.rotationQuaternion = finalRotation;
                }

                // Remove animation frame
                this.scene.unregisterBeforeRender(animationFrame);

                const index = this.springAnimationFrames.indexOf(animationFrame);
                if (index !== -1) {
                    this.springAnimationFrames.splice(index, 1);
                }

                this.animating = false;
                if (this.onAnimating) {
                    this.onAnimating(false);
                }

                return;
            }

            // Calculate ease-out progress (cubic ease out)
            const progress = elapsedTime / this.springDuration;
            const easedProgress = 1 - (1 - progress) ** 3;

            for (const item of initialRotations) {
                if (!item.rotation) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Create target rotation (only Y rotation preserved)
                const targetRotation = Quaternion.RotationAxis(
                    Vector3.Up(),
                    item.lastAlpha,
                );

                // Interpolate between initial and target rotation
                const interpolatedRotation = Quaternion.Slerp(
                    item.rotation,
                    targetRotation,
                    easedProgress,
                );

                // Apply interpolated rotation
                item.node.rotationQuaternion = interpolatedRotation;
            }
        };

        // Register animation frame
        this.scene.registerBeforeRender(animationFrame);
        this.springAnimationFrames.push(animationFrame);

        this.animating = true;
        if (this.onAnimating) {
            this.onAnimating(true);
        }
    }
}
